import { useEffect, useState } from "react";
import { DeviceMobile, AppleLogo, AndroidLogo, ShareNetwork, FingerprintSimple, Desktop } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import {
  isBiometricSupported,
  isPlatformAuthAvailable,
  enrollBiometric,
  getStored,
  clearStored,
} from "@/lib/biometric";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Install Mobile App page — the user can:
 *   1. Tap "Install App" → Chrome / Edge / Samsung Internet on Android
 *      will fire the native install prompt, dropping a real app icon on
 *      the home screen. iOS users get a "Share → Add to Home Screen" hint.
 *   2. Set up Biometric Unlock so future opens require FaceID / fingerprint
 *      only — no email + password retyping.
 */
export default function InstallApp() {
  const { user } = useAuth();
  const [installable, setInstallable] = useState(!!window.__pwaPrompt);
  const [installed, setInstalled] = useState(
    window.matchMedia("(display-mode: standalone)").matches
  );
  const [bioReady, setBioReady] = useState(false);
  const [bioEnrolled, setBioEnrolled] = useState(!!getStored());
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const onPrompt = () => setInstallable(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("pwa-installable", onPrompt);
    window.addEventListener("pwa-installed", onInstalled);
    (async () => setBioReady(await isPlatformAuthAvailable()))();
    return () => {
      window.removeEventListener("pwa-installable", onPrompt);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!window.__pwaPrompt) {
      setMsg("Your browser didn't expose an install prompt. Use the instructions below.");
      return;
    }
    setWorking(true);
    try {
      window.__pwaPrompt.prompt();
      const choice = await window.__pwaPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setMsg("Installing… check your home screen.");
      } else {
        setMsg("Install cancelled.");
      }
      window.__pwaPrompt = null;
      setInstallable(false);
    } finally {
      setWorking(false);
    }
  };

  const enableBiometric = async () => {
    setWorking(true);
    setMsg("");
    try {
      // Mint a fresh access token to bind to the device.
      const r = await axios.post(`${API}/auth/refresh`).catch(() => null);
      // Existing /auth/login also returns access_token now, so /me + manual
      // bind is a safe fallback.
      const me = await axios.get(`${API}/auth/me`).catch(() => null);
      const email = user?.email || me?.data?.email;
      const token = r?.data?.access_token || "";
      await enrollBiometric({ email, token });
      setBioEnrolled(true);
      setMsg("Biometric unlock enabled. Next time, tap the fingerprint icon on the login screen.");
    } catch (e) {
      setMsg(e.message || "Couldn't enable biometric unlock.");
    } finally {
      setWorking(false);
    }
  };

  const disableBiometric = () => {
    clearStored();
    setBioEnrolled(false);
    setMsg("Biometric unlock disabled.");
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl" data-testid="install-app-page">
      {/* Hero */}
      <div
        className="rounded-sm p-6 sm:p-10 mb-6 text-white"
        style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%)" }}
      >
        <div className="flex items-center gap-3 mb-2">
          <DeviceMobile size={36} weight="duotone" />
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Install App — Desktop &amp; Mobile</h1>
        </div>
        <p className="text-slate-300 text-sm max-w-xl">
          Install LokTech Dex Pro directly from this page — no app store, no downloads.
          Desktop gets a real software icon (opens in its own window),
          and on phones a home-screen app: fullscreen, offline support, FaceID / fingerprint unlock.
        </p>
      </div>

      {/* Step 1: Install */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <p className="text-[10px] font-black tracking-[0.3em] text-slate-400 mb-1">STEP 1</p>
        <h2 className="text-xl font-black text-slate-900 mb-2">Install on desktop or phone</h2>

        {installed ? (
          <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-sm text-emerald-800 text-sm font-bold">
            ✓ You&apos;re already running the installed app.
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">
              The button below triggers your browser&apos;s native install prompt. After install you&apos;ll
              see a <b>LokTech Dex Pro</b> icon on your home screen — tap it to open in fullscreen.
            </p>
            <button
              type="button"
              data-testid="pwa-install-btn"
              onClick={triggerInstall}
              disabled={working}
              className="w-full sm:w-auto px-6 py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-black tracking-widest text-sm rounded-sm transition"
            >
              {installable ? "↓ INSTALL APP" : "↓ INSTALL APP (browser hint below)"}
            </button>
            {msg && <p className="text-xs text-slate-500 mt-3">{msg}</p>}

            {/* Platform-specific instructions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="border-2 border-slate-900 rounded-sm p-4 md:col-span-2 bg-slate-50" data-testid="desktop-install-card">
                <div className="flex items-center gap-2 mb-2 text-slate-900">
                  <Desktop size={20} weight="fill" />
                  <h3 className="font-bold text-sm">Desktop / Laptop — Windows &amp; Mac (Chrome / Edge)</h3>
                </div>
                <ol className="text-xs text-slate-700 list-decimal pl-4 space-y-1">
                  <li>Open this website in the <b>Chrome or Edge</b> browser.</li>
                  <li>Click the <b>&ldquo;↓ INSTALL APP&rdquo;</b> button above — or click the <b>install icon (⊕ / monitor icon)</b> on the right side of the address bar.</li>
                  <li>Confirm <b>Install</b> — done! The <b>LokTech Dex Pro</b> icon will appear on your <b>Desktop and Start Menu</b>.</li>
                  <li>Now open the software directly from the desktop icon — it opens in its own window, just like regular software (no browser tabs).</li>
                </ol>
              </div>
              <div className="border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2 text-slate-800">
                  <AndroidLogo size={20} weight="fill" />
                  <h3 className="font-bold text-sm">Android (Chrome / Edge)</h3>
                </div>
                <ol className="text-xs text-slate-600 list-decimal pl-4 space-y-1">
                  <li>Open this page in Chrome (not in-app browser).</li>
                  <li>Tap the &ldquo;↓ INSTALL APP&rdquo; button above, or the ⋮ menu → <i>Install app</i>.</li>
                  <li>An icon labelled <b>LokTech Dex Pro</b> appears on your home screen.</li>
                </ol>
              </div>
              <div className="border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2 text-slate-800">
                  <AppleLogo size={20} weight="fill" />
                  <h3 className="font-bold text-sm">iPhone / iPad (Safari)</h3>
                </div>
                <ol className="text-xs text-slate-600 list-decimal pl-4 space-y-1">
                  <li>Open this page in Safari.</li>
                  <li>Tap the <ShareNetwork size={14} className="inline" /> Share button.</li>
                  <li>Choose <b>Add to Home Screen</b>.</li>
                  <li>Tap <b>Add</b> — the icon lands on your home screen.</li>
                </ol>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Step 2: Biometric */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <p className="text-[10px] font-black tracking-[0.3em] text-slate-400 mb-1">STEP 2</p>
        <div className="flex items-center gap-2 mb-2">
          <FingerprintSimple size={28} weight="duotone" />
          <h2 className="text-xl font-black text-slate-900">Enable Biometric Unlock</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Skip typing your password every time — unlock the app with the same FaceID /
          fingerprint sensor you use for your phone&apos;s banking app. Uses the device&apos;s native
          WebAuthn API; your biometric never leaves the phone.
        </p>

        {!bioReady ? (
          <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm text-amber-800 text-sm">
            Your current device or browser does not expose a biometric sensor. Try opening this
            page on your phone (Chrome on Android, Safari on iPhone).
          </div>
        ) : bioEnrolled ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-sm text-emerald-800 text-sm font-bold flex-1 min-w-[250px]">
              ✓ Biometric unlock is active for {getStored()?.email}
            </div>
            <button
              type="button"
              onClick={disableBiometric}
              data-testid="biometric-disable"
              className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-sm hover:bg-slate-50"
            >
              DISABLE
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="biometric-enable"
            onClick={enableBiometric}
            disabled={working}
            className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black tracking-widest text-sm rounded-sm transition flex items-center gap-2"
          >
            <FingerprintSimple size={20} weight="fill" />
            ENABLE BIOMETRIC UNLOCK
          </button>
        )}
      </div>
    </div>
  );
}
